import { useUserAvatar } from '@/hooks/useUserAvatar';
import { useUserExperiences } from '@/hooks/useUserExperiences';
import { useUserEducations } from '@/hooks/useUserEducations';
import { useUserProjects } from '@/hooks/useUserProjects';
import { useUserCertifications } from '@/hooks/useUserCertifications';
import { useMemo } from 'react';

interface ProfileCompletionResult {
  completionPercentage: number;
  suggestions: string[];
  isComplete: boolean;
  isBasicallyComplete: boolean;
  missingAreas: string[];
}

export const useProfileCompletion = () => {
  const { profile, user } = useUserAvatar();
  const { experiences, loading: experiencesLoading } = useUserExperiences();
  const { educations, loading: educationsLoading } = useUserEducations();
  const { projects, loading: projectsLoading } = useUserProjects();
  const { certifications, loading: certificationsLoading } = useUserCertifications();

  const loading = experiencesLoading || educationsLoading || projectsLoading || certificationsLoading;

  const completionData = useMemo((): ProfileCompletionResult => {
    if (loading) {
      return {
        completionPercentage: 0,
        suggestions: [],
        isComplete: false,
        isBasicallyComplete: false,
        missingAreas: []
      };
    }

    const checks = {
      // Basic Info (25% weight) 
      hasDisplayName: !!(profile?.first_name || profile?.last_name),
      hasEmail: !!(profile?.email || user?.email),
      hasBio: !!profile?.bio,
      hasLocation: !!profile?.location,
      hasPhone: !!profile?.phone,
      
      // Professional Experience (35% weight)
      hasExperience: experiences.length > 0,
      hasCurrentPosition: !!(profile?.current_position || profile?.job_title),
      hasCurrentCompany: !!profile?.current_company,
      hasSummary: !!profile?.summary,
      hasHeadline: !!profile?.headline,
      
      // Education (25% weight)  
      hasEducation: educations.length > 0,
      
      // Additional Info (15% weight)
      hasProjects: projects.length > 0,
      hasCertifications: certifications.length > 0,
      hasAvatar: !!profile?.avatar_url,
      hasWebsite: !!profile?.website,
      hasLinkedIn: !!profile?.linkedin_url,
      hasGitHub: !!profile?.github_url
    };

    // Calculate weighted completion percentage
    let totalScore = 0;
    let maxScore = 0;

    // Basic Info (25%)
    const basicInfoWeight = 25;
    const basicInfoChecks = [checks.hasDisplayName, checks.hasEmail, checks.hasBio, checks.hasLocation];
    const basicInfoScore = basicInfoChecks.filter(Boolean).length / basicInfoChecks.length;
    totalScore += basicInfoScore * basicInfoWeight;
    maxScore += basicInfoWeight;

    // Professional Experience (35%)
    const experienceWeight = 35;
    const professionalChecks = [checks.hasExperience, checks.hasCurrentPosition, checks.hasCurrentCompany, checks.hasSummary, checks.hasHeadline];
    const professionalScore = professionalChecks.filter(Boolean).length / professionalChecks.length;
    totalScore += professionalScore * experienceWeight;
    maxScore += experienceWeight;

    // Education (25%)
    const educationWeight = 25;
    totalScore += (checks.hasEducation ? 1 : 0) * educationWeight;
    maxScore += educationWeight;

    // Additional Info (15%)
    const additionalWeight = 15;
    const additionalChecks = [checks.hasProjects, checks.hasCertifications, checks.hasAvatar, checks.hasWebsite, checks.hasLinkedIn, checks.hasGitHub];
    const additionalScore = additionalChecks.filter(Boolean).length / additionalChecks.length;
    totalScore += additionalScore * additionalWeight;
    maxScore += additionalWeight;

    const completionPercentage = Math.round((totalScore / maxScore) * 100);

    // Check basic completion requirements for job applications
    const isBasicallyComplete = !!(
      (profile?.first_name || profile?.last_name) && // Has name
      (profile?.email || user?.email) && // Has email
      experiences.length > 0 && // Has at least one experience
      educations.length > 0 // Has at least one education
    );

    // Generate suggestions based on missing areas
    const suggestions: string[] = [];
    const missingAreas: string[] = [];

    if (!checks.hasExperience) {
      suggestions.push("Add your work experience to showcase your career journey");
      missingAreas.push("experience");
    }

    if (!checks.hasEducation) {
      suggestions.push("Add your education background to strengthen your profile");
      missingAreas.push("education");
    }

    if (!checks.hasDisplayName) {
      suggestions.push("Add your name to complete your basic profile");
      missingAreas.push("name");
    }

    if (!checks.hasAvatar) {
      suggestions.push("Upload a professional photo");
      missingAreas.push("photo");
    }

    if (!checks.hasBio && !checks.hasSummary) {
      suggestions.push("Add a professional summary or bio");
      missingAreas.push("summary");
    }

    if (!checks.hasCurrentPosition && !checks.hasCurrentCompany) {
      suggestions.push("Add your current position and company");
      missingAreas.push("current_role");
    }

    if (!checks.hasLocation) {
      suggestions.push("Add your location");
      missingAreas.push("location");
    }

    if (!checks.hasLinkedIn && !checks.hasWebsite) {
      suggestions.push("Add your LinkedIn profile or personal website");
      missingAreas.push("links");
    }

    // Prioritize most important suggestions
    const prioritizedSuggestions = suggestions.slice(0, 2);

    return {
      completionPercentage,
      suggestions: prioritizedSuggestions,
      isComplete: completionPercentage >= 90,
      isBasicallyComplete,
      missingAreas
    };
  }, [profile, user, experiences, educations, projects, certifications, loading]);

  return {
    ...completionData,
    loading
  };
};